import { useEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "../firebase-config";
import { reconcileClaims } from "../utils/claimsSync";

/**
 * Keeps the client's ID token in sync with the entitlements mirrored on the
 * Firestore user doc.
 *
 * The backend no longer revokes refresh tokens on an entitlement change — that
 * silently logged buyers out ~60 min after purchase (P1 fix). Instead it writes
 * the entitlements to users/{uid}, and this hook force-refreshes the token when
 * the mirror drifts from the token's claims. Paid features therefore unlock
 * within seconds of purchase without killing the session, and the refresh token
 * stays valid so the ~60-min ID-token refresh succeeds.
 *
 * Mounted once in AuthProvider. Kept separate from useEntitlements (which is a
 * pure read hook) so the token side effect lives in one place.
 */
export function useClaimSync(currentUser: User | null | undefined): void {
  // Guards against overlapping refreshes if several snapshots land before the
  // forced refresh resolves. Once the token matches the doc, reconcileClaims
  // is a no-op, so no infinite loop.
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!currentUser) return undefined;

    const ref = doc(db, "users", currentUser.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        const docEntitlements =
          data && Array.isArray(data.entitlements) ? data.entitlements : [];

        if (inFlightRef.current) return;
        inFlightRef.current = true;
        reconcileClaims({ user: currentUser, docEntitlements })
          .catch((err) => {
            console.warn(
              "[useClaimSync] claim reconcile failed:",
              err?.message || err,
            );
          })
          .finally(() => {
            inFlightRef.current = false;
          });
      },
      (err) => {
        console.warn(
          "[useClaimSync] user doc snapshot error:",
          err?.message || err,
        );
      },
    );

    return () => unsub();
  }, [currentUser]);
}
